import { Component, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { LobbyFormComponent } from "./lobby-form/lobby-form.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { LoaderService } from "../../../Utils/_services/loader.service";
import { ICreateFeaturedLobbyRequest, Lobby, LobbyStatusUI } from "../../../Utils/_models/lobbies.model";
import { ITheme, IThemeReq } from "../../../Utils/_models/themes.model";
import { AdminService } from "../../../Utils/_services/admin/admin/admin.service";
import { ThemesService } from "../../../Utils/_services/admin/Themes/themes.service";

import { TooltipModule } from "primeng/tooltip";
import { LobbyDetailsModalComponent } from "./lobby-details-modal/lobby-details-modal.component";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";


@Component({
  selector: "app-lobbies",
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, LobbyFormComponent, SearchBoxComponent, SpinnerLoaderComponent, LobbyDetailsModalComponent],
  templateUrl: "./lobbies.component.html",
  styleUrls: ["./lobbies.component.css"],
})
export class LobbiesComponent implements OnInit {

  searchTerm: string = "";
  selectedDate: string = "";
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalEntries: number = 0;
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 10;
  allSelected: boolean = false;
  lobbies: Lobby[] = [];
  filteredLobbies: Lobby[] = [];
  paginatedLobbies: Lobby[] = [];
  loading = signal(false);
  includeDeleted = false;
  creating = false;
  themes: ITheme[] = [];
  themesLoading = false;
  pageSizes: number[] = [5, 10, 20, 50];
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);

  showLobbyForm = false;
  selectedLobby: Lobby | null = null;
  isView = signal(false);
  previewPhotoUrl = signal<string | null>(null);

  constructor(
    private adminService: AdminService,
    private themeService: ThemesService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmService
  ) { }


  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchTerm = params['search'];
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { search: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
      this.fetchLobbies();
    });
  }

  fetchLobbies() {
    this.loading.set(true);
    const req = {
      Params: {
        page: this.currentPage,
        limit: this.itemsPerPage,
        type: 'all' as any,
        status: 'all' as any,
        search: this.searchTerm || undefined,
        includeDeleted: this.includeDeleted,
        date: this.selectedDate || undefined,
        sort: this.currentSortField(),
        sortOrder: this.currentSortField() ? this.currentSortOrder() : null
      }
    };

    this.adminService.getAllLobbies(req)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: any) => {
          const apiLobbies = res?.data?.lobbies ?? res?.lobbies ?? res?.data?.items ?? [];
          this.lobbies = apiLobbies.map((item: any) => this.mapLobby(item));
          this.totalEntries = res?.data?.totalCount ?? res?.totalCount ?? this.lobbies.length;

          this.filteredLobbies = [...this.lobbies];
          if (this.currentSortField() && this.currentSortOrder()) {
            this.sortLobbiesLocal();
          } else {
            this.updatePaginationServerSide();
          }
        },
        error: (err) => {
          console.error("Failed to fetch lobbies from API:", err);
          this.toastr.error("Failed to load lobbies from API.");
          this.lobbies = [];
          this.filteredLobbies = [];
          this.totalEntries = 0;
          this.updatePaginationServerSide();
        }
      });
  }
  openHostPreview(event: Event, lobby: Lobby): void {
    if (!lobby.hostPhotoUrl) return;
    event.stopPropagation();
    this.previewPhotoUrl.set(lobby.hostPhotoUrl);
  }

  closePreview(): void {
    this.previewPhotoUrl.set(null);
  }

  hasSelection(): boolean {
    return this.paginatedLobbies.some((l: any) => l.selected);
  }

  hasDeletedSelection(): boolean {
    return this.paginatedLobbies.some((l: any) => l.selected && l.isDeleted);
  }

  async deleteSelectedLobbies() {
    const selectedLobbies = this.paginatedLobbies.filter((l: any) => l.selected);
    const selectedIds = selectedLobbies.map(l => l._id);
    if (!selectedIds.length) return;

    const deletedSelected = selectedLobbies.filter(l => l.isDeleted);
    const activeSelected = selectedLobbies.filter(l => !l.isDeleted);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Lobbies' : 'Delete Selected Lobbies';
    const bodyHeader = isRestore ? 'Restore multiple lobbies' : 'Delete multiple lobbies';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted lobby(ies).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted lobby(ies) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected lobbies?`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss,
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      this.adminService.deleteLobbies(selectedIds).subscribe({
        next: (res) => {
          this.toastr.success(res?.data?.message || "Operation completed successfully");
          this.fetchLobbies();
        },
        error: (err) => {
          this.toastr.error("Failed to process lobbies");
        }
      });
    }
  }

  loadLobbies(): void {
    this.fetchLobbies();
  }

  onSearchInput(term: string): void {
    this.searchTerm = term;
    this.onSearch();
  }

  private mapStatus(apiStatus: string): LobbyStatusUI {
    const status = (apiStatus || "").toLowerCase();
    switch (status) {
      case "upcoming":
        return "Upcoming";
      case "active":
      case "in_progress":
        return "Live";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      default:
        return "Unknown";
    }
  }

  private mapLobby(item: any): Lobby {
    const hostSrc = item.hostId || item.createdBy || item.host || {};
    const fName = hostSrc.firstName || hostSrc.FirstName || '';
    const lName = hostSrc.lastName || hostSrc.LastName || '';
    const hostName = `${fName} ${lName}`.trim() || 'N/A';

    let initials = 'NA';
    if (fName || lName) {
      initials = `${fName ? fName[0] : ''}${lName ? lName[0] : ''}`.toUpperCase();
    }

    let photoUrl = '';
    if (hostSrc.ProfilePhotos && Array.isArray(hostSrc.ProfilePhotos) && hostSrc.ProfilePhotos.length > 0) {
      const primary = hostSrc.ProfilePhotos.find((p: any) => p.isPrimary) || hostSrc.ProfilePhotos[0];
      photoUrl = primary.signedUrl || primary.url || '';
    }

    return {
      _id: item._id,
      theme: item.name || (typeof item.theme === 'object' ? item.theme?.name : item.theme) || 'N/A',
      host: hostName,
      startTime: item.scheduledStartTime ? new Date(item.scheduledStartTime) : new Date(item.createdAt || Date.now()),
      price: item.entryFee ?? 0,
      entries: item.participantsList ? item.participantsList.length : (item.currentParticipants ?? 0),
      capacity: item.participantsCount ?? item.maxParticipants ?? 100,
      status: this.mapStatus(item.status),
      revenue: item.revenueGenerated ?? 0,
      selected: false,
      description: item.description || '',
      type: item.type || 'user_created',
      isRecurring: item.isRecurring || false,
      createdAt: item.createdAt ? new Date(item.createdAt) : undefined,
      hostEmail: hostSrc.email || '',
      interest: String(item.interestsList ? item.interestsList.length : 0),
      currentInterestIdx: item.currentInterestIdx ?? 0,
      interestsCount: item.interestsCount ?? (item.interestsList ? item.interestsList.length : 0),
      currentRound: item.currentRound ?? 1,
      participantsGender: item.participantsGender || 'N/A',
      interestsGender: item.interestsGender || 'N/A',
      totalRounds: item.totalRounds ?? 1,
      participantsList: item.participantsList || [],
      interestsList: item.interestsList || [],
      spectatorsList: item.spectatorsList || [],
      hostInitials: initials,
      hostPhotoUrl: photoUrl,
      isDeleted: item.isDeleted ?? false,
      deletedAt: item.deletedAt ? new Date(item.deletedAt) : null
    };
  }



  updatePaginationServerSide() {
    this.totalPages = Math.ceil(this.totalEntries / this.itemsPerPage);

    if (this.totalEntries === 0) {
      this.startIndex = 0;
      this.endIndex = 0;
    } else {
      this.startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
      this.endIndex = Math.min(
        this.currentPage * this.itemsPerPage,
        this.totalEntries
      );
    }

    this.paginatedLobbies = [...this.filteredLobbies];
    this.updateSelectAllState();
  }

  onSearch() {
    this.currentPage = 1;
    this.fetchLobbies();
  }

  onDateFilter() {
    this.currentPage = 1;
    this.fetchLobbies();
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortLobbiesLocal();
  }

  sortLobbiesLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.filteredLobbies = [...this.lobbies];
      this.updatePaginationServerSide();
      return;
    }

    const direction = order === "asc" ? 1 : -1;

    this.filteredLobbies.sort((a: any, b: any) => {
      let valA = a[field];
      let valB = b[field];

      if (field === 'host') {
        valA = a.host?.toLowerCase() || '';
        valB = b.host?.toLowerCase() || '';
      } else if (field === 'theme') {
        valA = a.theme?.toLowerCase() || '';
        valB = b.theme?.toLowerCase() || '';
      } else if (field === 'startTime') {
        valA = new Date(a.startTime).getTime();
        valB = new Date(b.startTime).getTime();
      } else if (field === 'price') {
        valA = Number(a.price || 0);
        valB = Number(b.price || 0);
      } else if (field === 'entries') {
        valA = Number(a.entries || 0);
        valB = Number(b.entries || 0);
      } else if (field === 'interest') {
        valA = Number(a.interest || 0);
        valB = Number(b.interest || 0);
      } else if (field === 'revenue') {
        valA = Number(a.revenue || 0);
        valB = Number(b.revenue || 0);
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.updatePaginationServerSide();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }

  selectAll() {
    this.allSelected = true;
    this.paginatedLobbies.forEach(lobby => (lobby.selected = true));
    this.updateSelectAllState();
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    this.paginatedLobbies.forEach(lobby => (lobby.selected = this.allSelected));
    this.updateSelectAllState();
  }

  onLobbySelect() {
    this.updateSelectAllState();
  }

  updateSelectAllState() {
    const selectedCount = this.paginatedLobbies.filter(
      lobby => lobby.selected
    ).length;
    this.allSelected =
      selectedCount === this.paginatedLobbies.length &&
      this.paginatedLobbies.length > 0;
  }



  onPageSizeChange(size: any) {
    this.itemsPerPage = Number(size);
    this.currentPage = 1;
    this.fetchLobbies();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchLobbies();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchLobbies();
    }
  }

  viewEdit(lobby: Lobby) {
    this.selectedLobby = lobby;
    this.isView.set(true);
  }

  closeView() {
    this.isView.set(false);
    this.selectedLobby = null;
  }

  onRowClick(event: Event, lobby: Lobby) {
    if ((event.target as HTMLElement).tagName.toLowerCase() === 'input') return;
    // Removed navigation per user request
  }



  closeLobbyForm() {
    this.showLobbyForm = false;
    this.selectedLobby = null;
  }

  handleLobbyFormSubmit(formData: ICreateFeaturedLobbyRequest) {
    this.creating = true;

    if (this.selectedLobby) {
      // Update existing lobby
      console.log("Updating lobby:", this.selectedLobby._id, formData);
      // TODO: Call update API when available
      this.toastr.info("Update functionality not yet implemented");
      this.creating = false;
      this.closeLobbyForm();
    } else {
      // Create new lobby
      this.adminService
        .createFeaturedLobby(formData)
        .pipe(finalize(() => (this.creating = false)))
        .subscribe({
          next: res => {
            console.log("Create Featured Lobby success:", res);
            this.closeLobbyForm();
            this.toastr.success("Featured lobby created successfully");
            this.fetchLobbies();
          },
          error: err => {
            console.error("Failed to create featured lobby", err);
            this.toastr.error("Failed to create featured lobby");
          },
        });
    }
  }

  trackByLobbyId(index: number, lobby: Lobby): string {
    return lobby._id;
  }
}
